import React from "react";
import useSWR from "swr";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { LinkSimple, LinkBreak } from "@phosphor-icons/react";
import { fetcher, fmtN } from "./common";

/** Lettrage des comptes de tiers : rapproche factures et règlements (somme nulle). */
export default function Lettrage({ file }) {
  const [prefix, setPrefix] = React.useState("411");
  const [sel, setSel] = React.useState({});
  const key = `/accounting/files/${file.id}/lettrage/candidates?prefix=${prefix}`;
  const { data, mutate: reload } = useSWR(key, fetcher);
  const rows = data?.rows || [];

  const selectedIds = Object.keys(sel).filter((k) => sel[k]);
  const selectedRows = rows.filter((r) => sel[r.entry_id]);
  const toLetter = selectedRows.filter((r) => !r.lettrage);
  const sum = toLetter.reduce((s, r) => s + r.amount, 0);
  const canLetter = toLetter.length >= 2 && Math.abs(sum) < 0.01;
  const canUnletter = selectedRows.some((r) => r.lettrage) && toLetter.length === 0;

  const toggleAll = (checked) => setSel(Object.fromEntries(rows.map((r) => [r.entry_id, checked])));

  const letter = async () => {
    try {
      const r = await api.put(`/accounting/files/${file.id}/lettrage`, { account_prefix: prefix, entry_ids: toLetter.map((x) => x.entry_id) });
      toast.success(`Lettrage ${r.data.code} appliqué à ${r.data.updated} écritures`);
      setSel({});
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Lettrage impossible"); }
  };

  const unletter = async () => {
    try {
      const ids = selectedRows.filter((r) => r.lettrage).map((r) => r.entry_id);
      await api.delete(`/accounting/files/${file.id}/lettrage`, { data: { account_prefix: prefix, entry_ids: ids } });
      toast.success("Lettrage retiré");
      setSel({});
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Délettrage impossible"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Select value={prefix} onValueChange={(v) => { setPrefix(v); setSel({}); }}>
            <SelectTrigger className="w-56" data-testid="lettrage-prefix"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="411">Clients (411)</SelectItem>
              <SelectItem value="401">Fournisseurs (401)</SelectItem>
              <SelectItem value="421">Personnel (421)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <label className="flex items-center gap-2 text-xs text-zinc-600 pb-1">
          <Checkbox checked={rows.length > 0 && selectedIds.length === rows.length} onCheckedChange={(v) => toggleAll(!!v)} data-testid="lettrage-toggle-all" />
          Tout sélectionner
        </label>
        <div className="text-sm pb-1" data-testid="lettrage-sum">
          Somme de la sélection : <b className={Math.abs(sum) < 0.01 && toLetter.length >= 2 ? "text-emerald-700" : "text-zinc-700"}>{fmtN(sum)}</b> {file.currency}
        </div>
        <div className="flex gap-2 pb-0.5">
          <Button onClick={letter} disabled={!canLetter} className="bg-[#2E3192] hover:bg-[#1E2066]" data-testid="lettrage-apply">
            <LinkSimple size={14} className="mr-1" /> Lettrer la sélection
          </Button>
          <Button variant="outline" onClick={unletter} disabled={!canUnletter} data-testid="lettrage-remove">
            <LinkBreak size={14} className="mr-1" /> Délettrer
          </Button>
        </div>
      </div>

      <div className="border border-zinc-200 bg-white rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500">
              <th className="px-3 py-2 w-10"></th><th className="px-3 py-2">Date</th><th className="px-3 py-2">Pièce</th>
              <th className="px-3 py-2">Libellé</th><th className="px-3 py-2 text-right">Débit</th><th className="px-3 py-2 text-right">Crédit</th><th className="px-3 py-2">Lettrage</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {rows.length === 0 && <tr><td colSpan={7} className="p-6 text-zinc-500">Aucun mouvement validé sur ce tiers.</td></tr>}
            {rows.map((r) => (
              <tr key={r.entry_id} className={r.lettrage ? "bg-zinc-50/60 text-zinc-400" : ""} data-testid={`lettrage-row-${r.entry_id}`}>
                <td className="px-3 py-2"><Checkbox checked={!!sel[r.entry_id]} onCheckedChange={(v) => setSel({ ...sel, [r.entry_id]: !!v })} data-testid={`lettrage-check-${r.entry_id}`} /></td>
                <td className="px-3 py-2">{r.date}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.piece}</td>
                <td className="px-3 py-2 truncate max-w-[240px]">{r.label || "—"}</td>
                <td className="px-3 py-2 text-right">{r.amount > 0 ? fmtN(r.amount) : ""}</td>
                <td className="px-3 py-2 text-right">{r.amount < 0 ? fmtN(-r.amount) : ""}</td>
                <td className="px-3 py-2">{r.lettrage && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 font-mono">{r.lettrage}</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-zinc-500">Sélectionnez les factures et règlements qui se compensent (somme nulle) pour les lettrer. Les lignes lettrées sont exclues de la balance âgée.</p>
    </div>
  );
}
